import type { OnwardQuoteSuggestion } from 'hooks/useOnwardBookings'
import { useHistory } from 'react-router-dom'
import { seedPickupFromArrival } from '../utils/onwardSeed'
import { useBookingWizardContext } from './useBookingWizard'

export interface OnwardCustomDestinationContext {
  arrivalAirport: OnwardQuoteSuggestion['arrivalAirport']
  arrivalDateTime: string
  flightNumber: string
}

interface UseOnwardActionsResult {
  selectSuggestion: (suggestion: OnwardQuoteSuggestion) => void
  selectCustomDestination: (context: OnwardCustomDestinationContext) => void
}

export const useOnwardActions = (): UseOnwardActionsResult => {
  const wizard = useBookingWizardContext()
  const history = useHistory()
  const {
    accountData,
    clearAll,
    setJourneyData,
    setAccountData,
    setStep,
    setIsOnwardJourney,
  } = wizard

  const selectSuggestion = (suggestion: OnwardQuoteSuggestion) => {
    const currentAccountData = { ...accountData }
    clearAll()
    setIsOnwardJourney(true)
    setJourneyData({
      journeyTypeName: 'OneWay',
      outbound: {
        pickup: {
          addressLine1: suggestion.arrivalAirport.name,
          formattedAddress: suggestion.arrivalAirport.name,
          latitude: suggestion.arrivalAirport.latitude,
          longitude: suggestion.arrivalAirport.longitude,
          countryCode: suggestion.arrivalAirport.countryCode,
          pointOfInterestTypeName: 'Airport',
        },
        dropOff: {
          addressLine1: suggestion.destination.addressLine1,
          addressLine2: suggestion.destination.addressLine2,
          formattedAddress: [
            suggestion.destination.addressLine1,
            suggestion.destination.town,
            suggestion.destination.postCode,
          ]
            .filter(Boolean)
            .join(', '),
          town: suggestion.destination.town,
          postCode: suggestion.destination.postCode,
          country: suggestion.destination.country,
          countryCode: suggestion.destination.countryCode,
          latitude: suggestion.destination.latitude,
          longitude: suggestion.destination.longitude,
        },
        when: seedPickupFromArrival(suggestion.arrivalDateTime),
        timeZone: suggestion.arrivalAirport?.timeZone,
        flightDetails: { flightNumber: suggestion.flightNumber },
      },
    })
    setAccountData(currentAccountData)
    setStep('journey')
    history.push('/admin/bookings/create')
  }

  const selectCustomDestination = ({
    arrivalAirport,
    arrivalDateTime,
    flightNumber,
  }: OnwardCustomDestinationContext) => {
    const currentAccountData = { ...accountData }
    clearAll()
    setIsOnwardJourney(true)
    setJourneyData({
      journeyTypeName: 'OneWay',
      outbound: {
        pickup: {
          addressLine1: arrivalAirport.name,
          formattedAddress: arrivalAirport.name,
          latitude: arrivalAirport.latitude,
          longitude: arrivalAirport.longitude,
          countryCode: arrivalAirport.countryCode,
          pointOfInterestTypeName: 'Airport',
        },
        when: seedPickupFromArrival(arrivalDateTime),
        timeZone: arrivalAirport.timeZone,
        flightDetails: { flightNumber },
      },
    })
    setAccountData(currentAccountData)
    setStep('journey')
    history.push('/admin/bookings/create')
  }

  return { selectSuggestion, selectCustomDestination }
}
